/**
 * `MyCertificationDetail` — dettaglio di una certificazione posseduta
 * dall'utente, raggiungibile dalla pagina "Le mie certificazioni" cliccando
 * l'intera card.
 *
 * Rotta `/certifications/:certificationId` (qualsiasi utente autenticato).
 * Rispetto al dettaglio della sezione "Livelli" NON mostra l'elenco degli altri
 * possessori (dato riservato): mostra invece i dati della propria assegnazione,
 * ossia le date di conseguimento/scadenza e il link al proprio badge specifico.
 *
 * Dati: `GET /api/me/certifications/:id/detail` → `MyCertificationDetailDto`
 * (accessibile solo se l'utente possiede la certificazione, altrimenti 404).
 */
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/useApiClient';
import type { MyCertificationDetailDto } from '../shared/dto';
import { certExpiryStatus, formatCertDate } from '../components/CertificationCard';
import { CertificationMedal } from '../components/CertificationMedal';
import {
  Alert,
  Badge,
  Button,
  Icon,
  Loading,
  Modal,
  PageHeader,
} from '../components/ui';

/** Renderizza la scadenza con lo stesso stato/tono della card certificazione. */
function ExpiryValue({ iso }: { iso: string | null }) {
  const status = certExpiryStatus(iso);
  if (status === 'none') return <>Nessuna</>;
  if (status === 'expired') {
    return <Badge tone="danger">{formatCertDate(iso)}</Badge>;
  }
  if (status === 'expiring') {
    return <Badge tone="warning">In scadenza {formatCertDate(iso)}</Badge>;
  }
  return <>{formatCertDate(iso)}</>;
}

export default function MyCertificationDetail() {
  const api = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { certificationId } = useParams<{ certificationId: string }>();

  // Provenienza passata dal link di origine (`state.from`): consente di tornare
  // alla pagina giusta e con l'etichetta coerente. Es. arrivando da "Il mio CV"
  // (`/assessment`) il back torna lì; altrimenti il default è l'elenco delle
  // proprie certificazioni (`/assessment/certifications`).
  const fromState = (location.state as { from?: string } | null)?.from;
  const backTo =
    fromState === '/assessment'
      ? { path: '/assessment', label: '← Torna al mio CV' }
      : {
          path: '/assessment/certifications',
          label: '← Torna alle mie certificazioni',
        };
  // `true` quando è aperta la conferma di "nascondi" della propria assegnazione.
  const [confirmHide, setConfirmHide] = useState(false);

  const { data, isLoading, isError, error } = useQuery<MyCertificationDetailDto>({
    queryKey: ['me', 'certifications', 'detail', certificationId],
    queryFn: () =>
      api.get<MyCertificationDetailDto>(
        `/api/me/certifications/${encodeURIComponent(certificationId ?? '')}/detail`,
      ),
    enabled: certificationId !== undefined && certificationId !== '',
  });

  const backToMine = () => {
    void navigate(backTo.path);
  };

  // "Nascondi" la propria assegnazione: non la elimina, la marca come nascosta
  // (DELETE /api/me/certifications/:id è una rimozione "soft"). Al termine
  // invalida la lista personale e il CV (dove compare l'elenco delle nascoste) e
  // torna alla pagina di provenienza.
  const hideCertification = useMutation({
    mutationFn: () =>
      api.delete<void>(
        `/api/me/certifications/${encodeURIComponent(certificationId ?? '')}`,
      ),
    onSuccess: () => {
      setConfirmHide(false);
      void queryClient.invalidateQueries({ queryKey: ['me', 'certifications'] });
      void queryClient.invalidateQueries({ queryKey: ['me', 'cv'] });
      backToMine();
    },
  });

  if (isLoading) {
    return <Loading>Caricamento certificazione…</Loading>;
  }

  if (isError || data === undefined) {
    const message =
      error instanceof Error
        ? error.message
        : 'Impossibile caricare la certificazione.';
    return (
      <section
        className="certification-detail"
        aria-labelledby="my-certification-detail-title"
      >
        <PageHeader
          title="Dettaglio certificazione"
          titleId="my-certification-detail-title"
          actions={
            <Button type="button" variant="ghost" onClick={backToMine}>
              {backTo.label}
            </Button>
          }
        />
        <Alert tone="error">{message}</Alert>
      </section>
    );
  }

  return (
    <section
      className="certification-detail"
      aria-labelledby="my-certification-detail-title"
    >
      <PageHeader
        title="Dettaglio certificazione"
        titleId="my-certification-detail-title"
        subtitle="I dettagli della certificazione e della tua assegnazione."
        actions={
          <>
            <Button type="button" variant="ghost" onClick={backToMine}>
              {backTo.label}
            </Button>{' '}
            <Button
              type="button"
              variant="danger"
              onClick={() => setConfirmHide(true)}
            >
              Nascondi dal mio profilo
            </Button>
          </>
        }
      />

      {hideCertification.isError && (
        <Alert tone="error" onDismiss={() => hideCertification.reset()}>
          Operazione non riuscita. Riprova più tardi.
        </Alert>
      )}

      {/* Hero: immagine grande, nome, ente, descrizione, skill e link alla
          pagina generale della certificazione. */}
      <div className="certification-detail__hero">
        <CertificationMedal
          imageUrl={data.imageUrl}
          name={data.name}
          issuer={data.issuer}
          backFields={[
            { label: 'Conseguita', value: formatCertDate(data.issuedAt) },
            {
              label: 'Scadenza',
              value:
                data.expiresAt === null ? 'Nessuna' : formatCertDate(data.expiresAt),
            },
          ]}
        />
        <div className="certification-detail__heading">
          <h2 className="certification-detail__name">{data.name}</h2>
          {data.issuer !== undefined && (
            <p className="certification-detail__issuer">{data.issuer}</p>
          )}
          {data.description !== undefined && (
            <p className="certification-detail__description">{data.description}</p>
          )}
          {data.skills.length > 0 && (
            <div className="certification-detail__skills">
              {data.skills.map((skill) => (
                <Badge key={skill.id} tone="neutral">
                  {skill.name}
                </Badge>
              ))}
            </div>
          )}
          {data.url !== undefined && (
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="certification-detail__link"
            >
              <Icon name="external" />
              <span>Vedi la certificazione</span>
            </a>
          )}

          {/* La mia assegnazione: date di conseguimento/scadenza, origine e link
              al proprio badge. I blocchi si dividono equamente lo spazio a destra
              dell'immagine. */}
          <dl className="certification-detail__meta">
            <div className="certification-detail__meta-item">
              <dt>Conseguita</dt>
              <dd>{formatCertDate(data.issuedAt)}</dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Scadenza</dt>
              <dd>
                <ExpiryValue iso={data.expiresAt} />
              </dd>
            </div>
            <div className="certification-detail__meta-item">
              <dt>Origine</dt>
              <dd>
                {data.source === 'credly' ? (
                  <Badge tone="info">Credly</Badge>
                ) : (
                  <Badge tone="neutral">Manuale</Badge>
                )}
              </dd>
            </div>
            {/* "Il tuo badge": mostrato solo quando l'URL è specifico della
                propria assegnazione, cioè diverso dall'URL generico già
                linkato sopra in "Vedi la certificazione". Il backend applica il
                fallback a `url`, quindi qui evitiamo il link duplicato. */}
            {data.badgeUrl !== undefined && data.badgeUrl !== data.url && (
              <div className="certification-detail__meta-item">
                <dt>Il tuo badge</dt>
                <dd>
                  <a
                    href={data.badgeUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="certification-detail__link"
                  >
                    <Icon name="external" />
                    <span>Apri il badge</span>
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Conferma di "nascondi" in modale. Non elimina nulla: la certificazione
          resta assegnata ma viene nascosta dal profilo, dagli insight e dal
          bonus, e può essere resa di nuovo visibile da "Il mio CV". */}
      <Modal
        open={confirmHide}
        onClose={() => {
          if (!hideCertification.isPending) setConfirmHide(false);
        }}
        title="Nascondere la certificazione?"
        footer={
          <>
            <Button
              type="button"
              variant="default"
              disabled={hideCertification.isPending}
              onClick={() => setConfirmHide(false)}
            >
              Annulla
            </Button>
            <Button
              type="button"
              variant="danger"
              loading={hideCertification.isPending}
              disabled={hideCertification.isPending}
              onClick={() => hideCertification.mutate()}
            >
              Nascondi
            </Button>
          </>
        }
      >
        <p>
          Stai per nascondere <strong>{data.name}</strong> dal tuo profilo. Non
          viene eliminata: sparisce dal tuo CV, dagli insight e dal calcolo dei
          bonus, ma la ritrovi nell'elenco delle certificazioni nascoste in "Il
          mio CV", da dove puoi renderla di nuovo visibile.
        </p>
      </Modal>
    </section>
  );
}
